/**
 * Small confirm dialog for the trainee XLSX export. Lets the trainer opt into
 * the full assessment matrix (every assigned score column, even if blank).
 */
import { useState } from 'react';
import { Download } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

export function ExportTraineesDialog({ open, onOpenChange, onExport, scopeLabel = 'all trainees' }) {
  const [includeAll, setIncludeAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try { await onExport(includeAll); onOpenChange(false); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-trainees-dialog">
        <DialogHeader>
          <DialogTitle>Export to Excel</DialogTitle>
          <DialogDescription>Downloads {scopeLabel} with every visible field as a column.</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer" htmlFor="export-include-all">
          <Checkbox id="export-include-all" checked={includeAll} onCheckedChange={v => setIncludeAll(!!v)}
                    className="mt-0.5" data-testid="export-include-all-checkbox" />
          <span>
            <Label htmlFor="export-include-all" className="cursor-pointer">Include all assessments</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Off: assessment columns with no score for anyone in this export are left out.
              On: every assigned assessment appears, even when blank.
            </p>
          </span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="export-confirm-btn">
            <Download className="w-4 h-4 mr-2" /> {busy ? 'Preparing…' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
